import { useState } from 'react';
import { PERSONAS } from '../lib/personas';
import { Quote, Shield, AlertTriangle, Wrench, Brain, ChevronDown, ChevronUp, Image } from 'lucide-react';

export function PersonasPage() {
  const [expandedId, setExpandedId] = useState<string | null>(PERSONAS[0].id);
  const [showPlaybook, setShowPlaybook] = useState<string | null>(null);

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-ink">Keystone Personas</h1>
        <p className="mt-2 max-w-2xl text-ink-secondary">
          Put yourself in their shoes. These 6 personas represent the real people
          your ideas will serve. Understanding their world is the difference between
          a clever idea and a breakthrough one.
        </p>
        <p className="mt-2 text-sm text-ink-tertiary">
          Based on the Keystone Persona Playbook — Byte by Yum! Experience Design 2026
        </p>
      </div>

      {/* Quick-select grid */}
      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {PERSONAS.map((p) => (
          <button
            key={p.id}
            onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
            className={`rounded-xl border p-3 text-center transition-all ${
              expandedId === p.id
                ? `${p.bg} ring-2 ring-offset-1 ring-current`
                : 'border-ring bg-surface-raised hover:shadow-card-hover'
            }`}
          >
            <span className="text-2xl">{p.emoji}</span>
            <p className={`mt-1 text-xs font-semibold ${expandedId === p.id ? p.color : 'text-ink'}`}>
              {p.name}
            </p>
            <p className="text-2xs text-ink-tertiary line-clamp-1">{p.title}</p>
          </button>
        ))}
      </div>

      {/* Expanded detail */}
      {PERSONAS.map((persona) => {
        if (expandedId !== persona.id) return null;
        return (
          <div key={persona.id} className={`animate-fade-in rounded-2xl border ${persona.bg} overflow-hidden`}>
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <span className="text-4xl">{persona.emoji}</span>
                <div className="flex-1">
                  <h2 className={`font-display text-2xl font-bold ${persona.color}`}>{persona.name}</h2>
                  <p className="text-sm text-ink-secondary">{persona.title}</p>
                  <p className="mt-2 text-sm text-ink">{persona.prompt}</p>
                </div>
              </div>

              <div className="mt-5 flex items-start gap-3 rounded-xl bg-surface-raised p-4 shadow-card">
                <Quote className="h-5 w-5 shrink-0 text-ink-tertiary mt-0.5" />
                <p className="text-base font-medium italic text-ink">"{persona.voice}"</p>
              </div>
            </div>

            <div className="border-t border-ring/30 bg-surface-raised/30 p-5 sm:p-6">
              <div className="grid gap-5 sm:grid-cols-2">
                <Section icon={<Shield className="h-4 w-4" />} title="What they're protecting" items={persona.protecting} color="text-green-700" />
                <Section icon={<AlertTriangle className="h-4 w-4" />} title="What breaks their day" items={persona.breaksDay} color="text-red-700" />
                <Section icon={<Wrench className="h-4 w-4" />} title="What they actually rely on" items={persona.reliesOn} color="text-blue-700" />
                <Section icon={<Brain className="h-4 w-4" />} title="How they think" items={persona.thinks} color="text-purple-700" italic />
              </div>
            </div>

            <div className="border-t border-ring/30 p-5 sm:p-6">
              <button
                onClick={() => setShowPlaybook(showPlaybook === persona.id ? null : persona.id)}
                className="flex w-full items-center gap-2 text-left"
              >
                <Image className="h-4 w-4 text-ink-secondary" />
                <span className="text-sm font-semibold text-ink">
                  Full Playbook Profile — {persona.name}
                </span>
                {showPlaybook === persona.id ? (
                  <ChevronUp className="ml-auto h-4 w-4 text-ink-tertiary" />
                ) : (
                  <ChevronDown className="ml-auto h-4 w-4 text-ink-tertiary" />
                )}
              </button>
              {showPlaybook === persona.id && (
                <div className="mt-4 animate-fade-in space-y-4">
                  {persona.playbookPages.map((pageNum) => (
                    <img
                      key={pageNum}
                      src={`/personas/page-${String(pageNum).padStart(2, '0')}.jpg`}
                      alt={`${persona.name} - Playbook Page ${pageNum}`}
                      className="w-full rounded-xl border border-ring shadow-card"
                      loading="lazy"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Section({
  icon, title, items, color, italic,
}: {
  icon: React.ReactNode; title: string; items: string[]; color: string; italic?: boolean;
}) {
  return (
    <div>
      <h3 className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${color}`}>
        {icon} {title}
      </h3>
      <ul className="mt-2.5 space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className={`text-sm leading-relaxed text-ink-secondary ${italic ? 'italic' : ''}`}>
            {italic ? `"${item}"` : `• ${item}`}
          </li>
        ))}
      </ul>
    </div>
  );
}
